import type { CampaignConfiguration, CampaignCostBasis, CampaignEconomicsInput } from './api';
const input = 'mt-1 w-full rounded-lg border border-border-subtle bg-bg-base px-3 py-2 text-sm';
const button =
  'min-h-11 rounded-lg border border-border-subtle px-4 py-2 text-sm disabled:opacity-40';
interface CampaignCostFormProps {
  configuration: CampaignConfiguration;
  onChange: (value: CampaignEconomicsInput | undefined) => void;
}
export function CampaignCostForm({ configuration, onChange }: CampaignCostFormProps) {
  const terms = {
    tier: configuration.tier,
    discountMinor: configuration.discountMinor,
    renewalMinor: configuration.renewalMinor,
  };
  const create = (): CampaignCostBasis => ({
    terms,
    netReceiptLowerMinor: null,
    variableCostUpperMinor: null,
    minimumContributionMinor: null,
    source: '',
    asOf: null,
    validUntil: null,
  });
  return (
    <details className="rounded-lg border border-border-subtle p-4">
      <summary className="cursor-pointer font-semibold">
        Kostenplanung · optional, nicht freigegeben
      </summary>
      <p className="mt-3 text-sm text-text-secondary">
        Ganze Cent, getrennt je Store. Nettozufluss nach Steuern und Storegebühren; übrige variable
        Kosten einschließlich RevenueCat, KI, Audio, Hosting und Risikoreserve. Leer bedeutet
        unbekannt. Null nur bei ausdrücklich belegtem Nullbetrag eingeben. Quellenreferenz ohne
        Schlüssel oder andere Geheimnisse. Finanzprüfung bleibt offen.
      </p>
      {(['APP_STORE', 'PLAY_STORE'] as const).map((provider) => {
        const label = provider === 'APP_STORE' ? 'Apple' : 'Google';
        const basis = configuration.economics?.[provider];
        const update = (patch: Partial<CampaignCostBasis>) => {
          if (basis) onChange({ ...configuration.economics, [provider]: { ...basis, ...patch } });
        };
        const mismatched =
          basis &&
          (basis.terms.tier !== terms.tier ||
            basis.terms.discountMinor !== terms.discountMinor ||
            basis.terms.renewalMinor !== terms.renewalMinor);
        return (
          <section
            key={provider}
            aria-label={`${label} Kostenplanung`}
            className="mt-4 space-y-3 border-t border-border-subtle pt-4"
          >
            <h3 className="font-semibold">{label}</h3>
            {!basis ? (
              <button
                type="button"
                className={button}
                disabled={terms.discountMinor === null || terms.renewalMinor === null}
                onClick={() => onChange({ ...configuration.economics, [provider]: create() })}
              >
                {label}: Kostengrundlage anlegen
              </button>
            ) : (
              <>
                {mismatched && (
                  <p role="alert" className="text-warning">
                    Tarif oder Preise geändert. Diese Grundlage ist nicht mehr anwendbar; bitte neu
                    anlegen.
                  </p>
                )}
                <div className="grid gap-3 md:grid-cols-3">
                  {(
                    [
                      ['netReceiptLowerMinor', 'Nettozufluss mindestens (Cent/Monat)'],
                      ['variableCostUpperMinor', 'Variable Kosten höchstens (Cent/Monat)'],
                      ['minimumContributionMinor', 'Mindestdeckungsbeitrag (Cent/Monat)'],
                    ] as const
                  ).map(([key, text]) => (
                    <label key={key} className="text-sm">
                      {label}: {text}
                      <input
                        className={input}
                        type="number"
                        min={0}
                        max={1000000}
                        step={1}
                        value={basis[key] ?? ''}
                        onChange={(e) =>
                          update({
                            [key]: e.target.value === '' ? null : e.target.valueAsNumber,
                          })
                        }
                      />
                    </label>
                  ))}
                </div>
                <label className="block text-sm">
                  {label}: Interne Quellenreferenz
                  <input
                    className={input}
                    maxLength={300}
                    value={basis.source}
                    onChange={(e) => update({ source: e.target.value })}
                  />
                </label>
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="text-sm">
                    {label}: Stand (UTC-Datum)
                    <input
                      className={input}
                      type="date"
                      value={basis.asOf?.slice(0, 10) ?? ''}
                      onChange={(e) =>
                        update({ asOf: e.target.value ? `${e.target.value}T00:00:00.000Z` : null })
                      }
                    />
                  </label>
                  <label className="text-sm">
                    {label}: Gültig einschließlich (UTC-Datum)
                    <input
                      className={input}
                      type="date"
                      value={basis.validUntil?.slice(0, 10) ?? ''}
                      onChange={(e) =>
                        update({
                          validUntil: e.target.value ? `${e.target.value}T23:59:59.999Z` : null,
                        })
                      }
                    />
                  </label>
                </div>
                <p className="text-xs text-text-secondary">
                  Gültigkeit muss die drei Rabattmonate nach dem Kampagnenende abdecken. Die Planung
                  setzt drei erfolgreiche Zahlungen voraus; keine Verlängerungs- oder
                  Kostengarantie.
                </p>
                {mismatched && (
                  <button
                    type="button"
                    className={button}
                    disabled={terms.discountMinor === null || terms.renewalMinor === null}
                    onClick={() => onChange({ ...configuration.economics, [provider]: create() })}
                  >
                    {label}: Grundlage leeren und neu binden
                  </button>
                )}
                <button
                  type="button"
                  className={button}
                  onClick={() => {
                    const next = { ...configuration.economics };
                    delete next[provider];
                    onChange(Object.keys(next).length ? next : undefined);
                  }}
                >
                  {label}: Kostengrundlage entfernen
                </button>
              </>
            )}
          </section>
        );
      })}
    </details>
  );
}
